/**
 * Liste paginée par curseur (« Afficher plus », §16.2). Lit `path?paged=1&…`
 * page après page et fusionne les pages sans doublon : une ligne qui change
 * de place entre deux pages n'apparaît qu'une fois. Les filtres font partie
 * de la clé, donc changer de filtre repart de la première page.
 *
 * Le retour se branche tel quel sur `QueryBoundary` (data, isLoading, error,
 * refetch) et sur `LoadMore` (hasMore, loadingMore, loadMore, loadMoreFailed).
 *
 * Une page suivante qui échoue ne remplace pas la liste par une erreur :
 * « Afficher plus » le dit et se réessaie (WEB2-01). Seule la première page se
 * relit chaque minute : une liste dépliée se relit au retour au premier plan
 * ou après une action, pas N requêtes par minute (WEB2-39).
 */
import { useMemo } from 'react';
import { useInfiniteQuery, type QueryKey } from '@tanstack/react-query';
import type { CursorPage } from '@myequip/shared';
import { api } from '../../core/api.js';

export type ListFilters = Record<string, string | number | boolean | null | undefined>;

/** Adresse d'une page : `paged=1`, les filtres renseignés, la taille et le curseur. */
export function cursorListUrl(path: string, filters: ListFilters, cursor?: string, limit?: number): string {
  const params = new URLSearchParams({ paged: '1' });
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  }
  if (limit) params.set('limit', String(limit));
  if (cursor) params.set('cursor', cursor);
  return `${path}${path.includes('?') ? '&' : '?'}${params.toString()}`;
}

/** Les éléments de toutes les pages lues, dans l'ordre, chacun une seule fois. */
export function mergePages<T extends { id: string }>(pages: readonly CursorPage<T>[]): T[] {
  const byId = new Map<string, T>();
  for (const page of pages) {
    for (const item of page.items) if (!byId.has(item.id)) byId.set(item.id, item);
  }
  return [...byId.values()];
}

export interface CursorList<T> {
  data: T[] | undefined;
  /** Nombre total de résultats, lu sur la première page quand la route le donne. */
  total: number | undefined;
  /** La première page telle que la route l'a servie (sommes ou compteurs qu'elle y joint). */
  first: CursorPage<T> | undefined;
  isLoading: boolean;
  /** Erreur de la liste elle-même ; jamais celle d'une page suivante (`loadMoreFailed`). */
  error: unknown;
  refetch: () => unknown;
  isPaused: boolean;
  dataUpdatedAt: number;
  hasMore: boolean;
  loadingMore: boolean;
  /** La dernière page suivante demandée n'a pas pu être lue. */
  loadMoreFailed: boolean;
  loadMore: () => void;
}

/** Rafraîchissement automatique : la première page seule, chaque minute. */
export const CURSOR_LIST_REFRESH_MS = 60_000;

export function cursorListRefetchInterval(loadedPages: number): number | false {
  return loadedPages > 1 ? false : CURSOR_LIST_REFRESH_MS;
}

export function useCursorList<T extends { id: string }>(options: {
  queryKey: QueryKey;
  path: string;
  filters?: ListFilters;
  limit?: number;
  enabled?: boolean;
  /**
   * Durée (ms) pendant laquelle une liste gardée en cache est servie sans être
   * relue. Par défaut celle de l'application ; `0` pour une file que d'autres
   * écrans changent (une demande planifiée ailleurs n'y reste pas affichée).
   */
  staleTime?: number;
}): CursorList<T> {
  const filters = options.filters ?? {};
  const query = useInfiniteQuery({
    queryKey: [...options.queryKey, 'paged', filters, options.limit ?? null],
    initialPageParam: '',
    queryFn: ({ pageParam, signal }) =>
      api<CursorPage<T>>(cursorListUrl(options.path, filters, pageParam || undefined, options.limit), { signal }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: (q) => cursorListRefetchInterval(q.state.data?.pages.length ?? 0),
    enabled: options.enabled ?? true,
    ...(options.staleTime === undefined ? {} : { staleTime: options.staleTime }),
  });
  const pages = query.data?.pages;
  const data = useMemo(() => (pages ? mergePages(pages) : undefined), [pages]);
  return {
    data,
    total: pages?.[0]?.total,
    first: pages?.[0],
    isLoading: query.isLoading,
    error: query.isFetchNextPageError ? null : query.error,
    refetch: query.refetch,
    isPaused: query.isPaused,
    dataUpdatedAt: query.dataUpdatedAt,
    hasMore: query.hasNextPage,
    loadingMore: query.isFetchingNextPage,
    loadMoreFailed: query.isFetchNextPageError,
    loadMore: () => {
      if (query.hasNextPage && !query.isFetchingNextPage) void query.fetchNextPage();
    },
  };
}
